import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';

/**
 * Catches a crash while drawing a page, so a mistake in one view shows a
 * message instead of an empty screen, and the rest of PST stays usable.
 * `resetKey` (the path) clears it when moving to another page.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('PST view crashed', error, info.componentStack);
  }

  override componentDidUpdate(previous: { resetKey: string }): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <Alert color="red" title="Diese Ansicht ist auf einen Fehler gelaufen" m="md">
        <Text size="sm">Gespeichertes ist nicht verloren. Neu laden stellt die Ansicht wieder her.</Text>
        <Text size="xs" c="dimmed" ff="monospace" mt={4}>{this.state.error.message}</Text>
        <Group mt="sm">
          <Button size="xs" onClick={() => window.location.reload()}>Neu laden</Button>
          <Button size="xs" variant="default" onClick={() => this.setState({ error: null })}>Erneut versuchen</Button>
        </Group>
      </Alert>
    );
  }
}
